import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { useSession } from "@/services/auth";
import type {
  PickupAvailabilityStatus,
  PickupServiceDay,
} from "@/api.types.v2";

/**
 * Phase 11b (#204): hooks for the schedule-pickup-from-scratch wizard.
 *
 * Backed by four endpoints:
 *   GET  /pickup-options-for-address       (step 2: service area + tiers + slots)
 *   GET  /pickup-bookings/eligible-shipments (step 1: which shipments to attach)
 *   POST /pickup-bookings/preview            (step 3: cost preview)
 *   POST /pickup-bookings                    (submit)
 *
 * ezx-server PR #276: endpoints now key on raw `Address.id` rather than
 * `AddressBookEntry.id`. The eligible-shipments endpoint takes no query
 * params and tags each shipment with its `senderAddressId` so the client
 * groups them inline.
 */

const API_URL = import.meta.env.VITE_API_URL;

export interface PickupOptions {
  serviceAreaCovered: boolean;
  /** IANA tz of the dispatch routing this address (null when uncovered). */
  dispatchTimeZone: string | null;
  /** City label for "Pickup time shown in {city} time" (null when uncovered). */
  dispatchCity: string | null;
  dateAvailability: {
    status: PickupAvailabilityStatus;
    nextAvailableDate: string | null;
  } | null;
  pickupServices: {
    id: string;
    code: string;
    name: string;
    dispatch: { id: string; timeZone: string; city: string | null };
    dateAvailability?: PickupOptions["dateAvailability"];
    price?: {
      kind: "BASELINE";
      subtotalCents: number;
      taxCents: number;
      totalCents: number;
      currency: "CAD";
    };
  }[];
  timeSlots: {
    pickupServiceId: string;
    id: string;
    name: string;
    startTime: string;
    endTime: string;
    serviceDays: PickupServiceDay[];
    /** Minutes before slot start when bookings stop accepting (client-side cutoff filter). */
    bookingLeadMinutes: number;
    /** Remaining capacity for the requested date; null = unlimited; absent when no date passed. */
    remaining: number | null;
    availabilityStatus: PickupAvailabilityStatus | null;
  }[];
}

export interface EligibleShipment {
  id: string;
  shipmentNumber: number;
  trackingNumber: string | null;
  carrierId: string | null;
  /** Raw `Address.id` of the shipment's sender — null when shipment has no sender address yet. */
  senderAddressId: string | null;
  /** Snapshot of the sender address fields (null when shipment has no sender address). */
  senderAddress: {
    line1: string | null;
    line2: string | null;
    city: string | null;
    province: string | null;
    postalCode: string | null;
  } | null;
  order: {
    id: string;
    orderNumber: number;
    paymentMethodType: string | null;
    paymentStatus: string;
    paymentTermsId: string | null;
    invoicePending: boolean;
    paymentTermsRef: {
      id: string;
      code: string;
      name: string;
      paymentDueDays: number;
    } | null;
  };
  packages: {
    weightGrams: number;
    lengthCm: number | string;
    widthCm: number | string;
    heightCm: number | string;
  }[];
  tasks: { contactName: string | null }[];
  pickupEligibility?: {
    eligible: boolean;
    reason: PickupIneligibilityReason | null;
  };
}

export type PickupIneligibilityReason =
  | "ALREADY_RECEIVED"
  | "WORK_COMPLETED"
  | "WORK_STARTED"
  | "TASK_UNAVAILABLE"
  | "TRIP_STARTED"
  | "ALREADY_BOOKED"
  | "SHIPMENT_NOT_READY";
export interface PickupIneligibleShipment {
  id: string;
  shipmentNumber?: number;
  reason: PickupIneligibilityReason;
}

export function pickupIneligibleShipments(
  err: unknown
): PickupIneligibleShipment[] {
  if (
    !isPickupBookingError(err) ||
    err.response?.data.error !== "SHIPMENT_INELIGIBLE"
  )
    return [];
  const issues = err.response.data.details?.shipments;
  if (!Array.isArray(issues)) return [];
  return issues.flatMap((issue: unknown) => {
    if (
      !issue ||
      typeof issue !== "object" ||
      !("id" in issue) ||
      typeof issue.id !== "string" ||
      !("shipmentNumber" in issue) ||
      typeof issue.shipmentNumber !== "number" ||
      !("reason" in issue)
    )
      return [];
    const reason = issue.reason;
    return [
      {
        id: issue.id,
        shipmentNumber: issue.shipmentNumber,
        reason:
          reason === "ALREADY_RECEIVED" ||
          reason === "WORK_COMPLETED" ||
          reason === "WORK_STARTED" ||
          reason === "TRIP_STARTED" ||
          reason === "ALREADY_BOOKED" ||
          reason === "SHIPMENT_NOT_READY"
            ? reason
            : "TASK_UNAVAILABLE",
      },
    ];
  });
}

export interface PickupPreview {
  /** Server-evaluated dispatch for this exact reviewed selection. */
  dispatch: { id: string; timeZone: string; city: string | null };
  pickupAddress?: NonNullable<EligibleShipment["senderAddress"]> & {
    id: string;
    country: string;
  };
  pickupContact?: PickupContact;
  addressBookContact?: PickupContact;
  addressBookAddressId?: string;
  bookingSnapshot?: {
    bookingId: string;
    pickupAddressId: string;
    pickupServiceId: string;
    dispatchCenterId: string;
    scheduledDate: string;
    slotId: string;
    shipmentIds: string[];
  };
  deliveryConsequences?: {
    shipmentId: string;
    previousEstimatedDelivery: string | null;
    estimatedDelivery: string | null;
    /** Prospective per-Shipment consent, Server c4002159. Round-trip unchanged. */
    planning: {
      timing: {
        scheduledDate: string;
        slotId: string;
        startTime: string;
        endTime: string;
        timeZone: string;
      };
      readyAt: string | null;
      handoffBy: string | null;
      atCarrierFastest: string | null;
      deliveredSlowest: string;
      deliveredPrecision: "DATE" | "DATETIME";
    };
  }[];
  /** NET pickup subtotal (raw - bundleDiscount), pre-tax. */
  totalCents: number;
  /** Output tax on the net subtotal (origin/pickup-address province rate). */
  taxCents: number;
  /** Phase 12: sum of raw rate-band quotes pre-discount. Equals totalCents
   *  when no bundle discount applies. */
  rawTotalCents: number;
  /** Phase 12: total bundle savings on this preview (sum of per-shipment discounts). */
  bundleDiscountCents: number;
  currency: string;
  shipments: {
    id: string;
    shipmentNumber: number;
    /** Raw rate-band quote (full price). Server stores this as Shipment.pickupPriceCents. */
    pickupPriceCents: number;
    /** Phase 12: discount allocated to this shipment based on bundle position. */
    bundleDiscountCents: number;
    /** Phase 12: net amount this shipment contributes to the order's charge. */
    netPickupCents: number;
  }[];
}

export interface PickupContact {
  contactName: string;
  contactCompany: string | null;
  contactPhone: string | null;
  contactEmail: string | null;
  instructions: string | null;
  isResidential: boolean | null;
}

export interface CreateBookingResult {
  booking: { id: string; scheduledDate: string };
  attachedShipmentIds: string[];
  paymentIds: string[];
  fromIdempotencyCache: boolean;
}

export type PickupPaymentMethodType = "SAVED_CARD" | "CREDIT" | "INVOICE";

/** Fetch available services/times for an owned saved entry and selected parcels. */
export function usePickupOptionsForAddress(
  addressBookEntryId: string | null,
  shipmentIds: string[],
  date: string | null
) {
  const { token, customer } = useSession();
  const shipmentIdsKey = [...shipmentIds].sort().join(",");
  return useQuery<PickupOptions>({
    queryKey: [
      "pickupOptionsForAddress",
      customer?.id,
      addressBookEntryId,
      shipmentIdsKey,
      date,
    ],
    queryFn: async () => {
      const { data } = await axios.get<{ data: PickupOptions }>(
        `${API_URL}/api/v2/customers/me/pickup-options-for-address`,
        {
          params: {
            addressBookEntryId,
            shipmentIds: shipmentIdsKey,
            ...(date && { date }),
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.data;
    },
    enabled:
      !!token &&
      !!customer?.id &&
      !!addressBookEntryId &&
      shipmentIds.length > 0,
    staleTime: 60_000,
  });
}

/**
 * Fetch all label-ready shipments the customer can put on a pickup. Server
 * returns each shipment's `senderAddressId` so the UI can group them by
 * sender address (a single pickup booking is one address).
 */
export function useEligiblePickupShipments() {
  const { token, customer } = useSession();
  return useQuery<EligibleShipment[]>({
    queryKey: ["eligiblePickupShipments", customer?.id],
    queryFn: async () => {
      const { data } = await axios.get<{ data: EligibleShipment[] }>(
        `${API_URL}/api/v2/pickup-bookings/eligible-shipments`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return data.data;
    },
    enabled: !!token && !!customer?.id,
    staleTime: 30_000,
  });
}

interface PreviewArgs {
  bookingId?: string;
  addressBookEntryId?: string;
  pickupAddressId?: string;
  pickupServiceId: string;
  shipmentIds: string[];
  /** Phase 12 (#232): when provided, server checks for an existing booking
   *  at (addr, date, slot, service, customer) and seeds the bundle position
   *  counter accordingly. Without these, response shows position-0 baseline. */
  scheduledDate?: string; // YYYY-MM-DD
  slotId?: string;
}

export function usePreviewPickupBooking() {
  const { token } = useSession();
  return useMutation({
    mutationFn: async (args: PreviewArgs) => {
      const { data } = await axios.post<{ data: PickupPreview }>(
        `${API_URL}/api/v2/pickup-bookings/preview`,
        args,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}

interface CreateArgs extends PreviewArgs {
  expectedQuote?: PickupPreview;
  scheduledDate: string; // YYYY-MM-DD
  slotId: string;
  paymentMethodType: PickupPaymentMethodType;
  paymentMethodId?: string | null;
  notes?: string | null;
  /** Idempotency key generated client-side; one per submit attempt. */
  idempotencyKey: string;
}

export function useCreatePickupBooking() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();
  return useMutation({
    mutationFn: async (args: CreateArgs) => {
      const { idempotencyKey, ...body } = args;
      const { data } = await axios.post<{ data: CreateBookingResult }>(
        `${API_URL}/api/v2/pickup-bookings`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Idempotency-Key": idempotencyKey,
          },
        }
      );
      return data.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["myPickupBookings", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["eligiblePickupShipments", customer?.id],
      });
    },
  });
}

/**
 * Phase 16c: attach existing shipments to an existing pickup booking.
 * Companion to `useCreatePickupBooking`; same idempotency contract.
 *
 * POSTs to: /api/v2/pickup-bookings/:id/shipments
 */
interface AttachArgs {
  expectedQuote?: PickupPreview;
  bookingId: string;
  shipmentIds: string[];
  paymentMethodType: PickupPaymentMethodType;
  paymentMethodId?: string | null;
  idempotencyKey: string;
}

export function useAttachShipmentsToBooking() {
  const queryClient = useQueryClient();
  const { token, customer } = useSession();
  return useMutation({
    mutationFn: async (args: AttachArgs) => {
      const { bookingId, idempotencyKey, ...body } = args;
      const { data } = await axios.post<{ data: CreateBookingResult }>(
        `${API_URL}/api/v2/pickup-bookings/${bookingId}/shipments`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Idempotency-Key": idempotencyKey,
          },
        }
      );
      return data.data;
    },
    onSuccess: async () => {
      // Same invalidation surface as the create path — attach affects the
      // same list views and the same eligibility query.
      await queryClient.invalidateQueries({
        queryKey: ["myPickupBookings", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["customerOrdersV2", customer?.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ["eligiblePickupShipments", customer?.id],
      });
    },
  });
}

/** Type guard for the server's structured error responses. */
export function isPickupBookingError(err: unknown): err is AxiosError<{
  error: string;
  message?: string;
  details?: Record<string, unknown>;
}> {
  return (
    axios.isAxiosError(err) &&
    typeof err.response?.data === "object" &&
    err.response.data !== null &&
    "error" in err.response.data
  );
}
